<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import powerDataAPI from 'dashboard/api/powerData';
import { fraseDaRecusa } from 'dashboard/helper/recusaDoAcesso';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { lerCsvQa } from '../helpers/csvQa';
import {
  ACCEPT_ARQUIVO,
  MAX_BYTES_MIDIA,
  ehMidia,
  motivoDoErro,
} from '../helpers/midia';

const props = defineProps({
  storeId: { type: [String, Number], required: true },
});
const emit = defineEmits(['added']);

const { t } = useI18n();
const appStore = useStore();
const accountId = appStore.getters.getCurrentAccountId;

const dialogRef = ref(null);
// 'tipo' = escolha do tipo; 'form' = o formulário daquele tipo.
const etapa = ref('tipo');
const tipo = ref(null);

const files = ref([]);
const isDragging = ref(false);
const textContent = ref('');
const textFilename = ref('');
// Cada par tem as SUAS tags: e o que permite filtrar "garantia" sem arrastar o
// resto do lote. As tags do envio (abaixo) continuam existindo e sao somadas a
// todas — serve para o que vale para o lote inteiro ("sac", "2026").
const qaPairs = ref([{ question: '', answer: '', tags: [] }]);
const gerandoTags = ref(false);
const csvInput = ref(null);
const csvAviso = ref('');
const tags = ref([]);
const isUploading = ref(false);
const errorMessage = ref('');
const batch = ref([]);

// ── Site: descobrir antes de importar ─────────────────────────────
// A pessoa cola a URL, vê a lista de páginas com checkbox, filtra por
// caminho e só então importa. Evita o padrão "importar 800, apagar 600".
const siteUrl = ref('');
const sitePages = ref([]);
const siteSelecionadas = ref(new Set());
const siteIncluir = ref('');
const siteExcluir = ref('');
const isDiscovering = ref(false);
const siteEngine = ref('');
function normalizarUrl(u) {
  const v = (u || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

const siteVia = ref('');
// Timer do acompanhamento da descoberta. Guardado num ref para o `reset()` poder
// matar: o dialogo fecha por Esc e pelo evento nativo, e um timer sobrevivente
// ficaria consultando uma descoberta que ninguem mais olha.
let descobertaTimer = null;
const DESCOBERTA_POLL_MIN = 1500;
const DESCOBERTA_POLL_MAX = 8000;
// Teto de paciencia. O motor tem timeout proprio de 60s; damos folga e desistimos
// com mensagem em vez de consultar para sempre.
const DESCOBERTA_PRAZO_MS = 120000;
// Como as páginas entram na base. 'descobrir' é o caminho antigo (mapeia o site e
// a pessoa marca); os outros dois existem porque pulam o mapeamento: quem já sabe
// o endereço não precisa que o motor adivinhe.
//
// Importar o site inteiro é só da plataforma por enquanto (decisão do Murilo, 23/09/2026): para
// os outros sobra a página única, e o servidor recusa as outras rotas a quem não é super admin.
const { isSuperAdmin } = useAdmin();
const modoInicial = () => (isSuperAdmin.value ? 'descobrir' : 'pagina');
const siteModo = ref(modoInicial());
const siteUrlsColadas = ref('');

// Opções de limpeza e acesso, por importação. O padrão é o do motor; mexer aqui é
// a exceção, então o bloco começa fechado.
const opcoesAbertas = ref(false);
const opcExcluirTags = ref('');
const opcIncluirTags = ref('');
const opcCookies = ref('');
const opcSoPrincipal = ref(true);

function listaDeTags(texto) {
  return (texto || '')
    .split(/[,\n]/)
    .map(item => item.trim())
    .filter(Boolean);
}

// `null` quando nada foi mexido: ausência quer dizer "usa o padrão do motor", e um
// objeto com todos os campos vazios diria a mesma coisa parecendo escolha.
const opcoesDeScrape = computed(() => {
  const o = {
    excluir_tags: listaDeTags(opcExcluirTags.value),
    incluir_apenas_tags: listaDeTags(opcIncluirTags.value),
    cookies: opcCookies.value.trim(),
    so_conteudo_principal: opcSoPrincipal.value,
  };
  const mexido =
    o.excluir_tags.length ||
    o.incluir_apenas_tags.length ||
    o.cookies ||
    !o.so_conteudo_principal;
  return mexido ? o : null;
});

// URLs coladas, uma por linha. Dedup porque lista colada de planilha repete.
const urlsColadas = computed(() => [
  ...new Set(
    siteUrlsColadas.value
      .split(/\s+/)
      .map(u => normalizarUrl(u))
      .filter(u => /^https?:\/\/[^\s]+\.[^\s]+/i.test(u))
  ),
]);

function caminhoDe(url) {
  try {
    return new URL(url).pathname || '/';
  } catch {
    return url;
  }
}

function casaPadrao(caminho, padroes) {
  const lista = (padroes || '')
    .split(/[,\n]/)
    .map(x => x.trim())
    .filter(Boolean);
  if (!lista.length) return null;
  return lista.some(p => caminho.startsWith(p) || caminho.includes(p));
}

// Páginas visíveis = descobertas ∩ incluir − excluir. Filtro é local e
// instantâneo: a descoberta é uma chamada só, o refino é grátis.
const sitePagesFiltradas = computed(() =>
  sitePages.value.filter(pg => {
    const c = caminhoDe(pg.url);
    const inc = casaPadrao(c, siteIncluir.value);
    const exc = casaPadrao(c, siteExcluir.value);
    if (inc === false) return false;
    if (exc === true) return false;
    return true;
  })
);

const siteTodasMarcadas = computed(
  () =>
    sitePagesFiltradas.value.length > 0 &&
    sitePagesFiltradas.value.every(pg => siteSelecionadas.value.has(pg.url))
);

function toggleSitePagina(url) {
  const prox = new Set(siteSelecionadas.value);
  if (prox.has(url)) prox.delete(url);
  else prox.add(url);
  siteSelecionadas.value = prox;
}

function toggleSiteTodas() {
  siteSelecionadas.value = siteTodasMarcadas.value
    ? new Set()
    : new Set(sitePagesFiltradas.value.map(pg => pg.url));
}

// Consulta o andamento até sair de 'rodando'. Recuo progressivo para não martelar
// o serviço num mapeamento longo, e um prazo máximo para não consultar para sempre.
function acompanharDescoberta(descobertaId) {
  if (!descobertaId) {
    errorMessage.value = t('POWER_DATA.ADD.SITE_DISCOVER_ERROR');
    isDiscovering.value = false;
    return Promise.resolve();
  }
  const comecou = Date.now();
  let espera = DESCOBERTA_POLL_MIN;
  return new Promise(resolve => {
    const consultar = async () => {
      try {
        const st = await powerDataAPI.sourcesSiteDiscoverStatus(
          accountId,
          props.storeId,
          descobertaId
        );
        if (st?.status === 'rodando') {
          if (Date.now() - comecou > DESCOBERTA_PRAZO_MS) {
            errorMessage.value = t('POWER_DATA.ADD.SITE_DISCOVER_SLOW');
            isDiscovering.value = false;
            resolve();
            return;
          }
          espera = Math.min(Math.round(espera * 1.4), DESCOBERTA_POLL_MAX);
          descobertaTimer = setTimeout(consultar, espera);
          return;
        }
        if (st?.status === 'erro') {
          errorMessage.value = t('POWER_DATA.ADD.SITE_DISCOVER_ERROR');
        } else {
          sitePages.value = st?.pages || [];
          siteEngine.value = st?.engine || '';
          siteVia.value = st?.via || siteVia.value;
          // Começa com tudo marcado: o caso comum é "quero o site"; desmarcar é a exceção.
          siteSelecionadas.value = new Set(sitePages.value.map(pg => pg.url));
          if (!sitePages.value.length)
            errorMessage.value = t('POWER_DATA.ADD.SITE_NONE_FOUND');
        }
      } catch {
        errorMessage.value = t('POWER_DATA.ADD.SITE_DISCOVER_ERROR');
      }
      // Fora do `try` de propósito: uma exceção aqui dentro já matou o poll uma vez
      // neste arquivo (ver DatasourcesTab), e o sintoma é a tela girando para sempre.
      isDiscovering.value = false;
      resolve();
    };
    consultar();
  });
}

async function descobrirPaginas() {
  const url = normalizarUrl(siteUrl.value);
  if (!url) return;
  isDiscovering.value = true;
  errorMessage.value = '';
  sitePages.value = [];
  siteSelecionadas.value = new Set();
  try {
    const data = await powerDataAPI.sourcesSiteDiscover(
      accountId,
      props.storeId,
      url,
      300,
      siteModo.value === 'sitemap'
    );
    if (data?.error) throw new Error(data.error?.message || 'discover failed');
    // 'sitemap' ou 'map': os dois custam coisas diferentes (o mapa gasta crédito,
    // o sitemap é um GET de XML), e quem olha a lista merece saber qual respondeu.
    siteVia.value = data?.via || '';
    await acompanharDescoberta(data?.discovery_id);
  } catch (e) {
    errorMessage.value =
      e?.codigo === 'url_interna'
        ? t('POWER_DATA.ADD.SITE_URL_INTERNA')
        : t('POWER_DATA.ADD.SITE_DISCOVER_ERROR');
    isDiscovering.value = false;
  }
}

// Uma condição por modo: a lista colada e a página única não passam pela
// descoberta, então exigir páginas marcadas travaria o botão para sempre.
const sitePronto = computed(() => {
  if (siteModo.value === 'colar') return urlsColadas.value.length > 0;
  if (siteModo.value === 'pagina') return !!normalizarUrl(siteUrl.value);
  return sitePagesFiltradas.value.some(pg =>
    siteSelecionadas.value.has(pg.url)
  );
});

const MODOS_DE_SITE = computed(() =>
  [
    { key: 'descobrir', label: t('POWER_DATA.ADD.SITE_MODE_DISCOVER') },
    { key: 'sitemap', label: t('POWER_DATA.ADD.SITE_MODE_SITEMAP') },
    { key: 'colar', label: t('POWER_DATA.ADD.SITE_MODE_PASTE') },
    { key: 'pagina', label: t('POWER_DATA.ADD.SITE_MODE_SINGLE') },
  ].filter(modo => isSuperAdmin.value || modo.key === 'pagina')
);

const dicaDoModo = computed(() =>
  t(`POWER_DATA.ADD.SITE_MODE_HINT_${siteModo.value.toUpperCase()}`)
);

const rotuloDaUrl = computed(() => {
  if (siteModo.value === 'sitemap') return t('POWER_DATA.ADD.SITE_SITEMAP_URL');
  if (siteModo.value === 'pagina') return t('POWER_DATA.ADD.SITE_PAGE_URL');
  return t('POWER_DATA.ADD.SITE_URL');
});

const placeholderDaUrl = computed(() => {
  if (siteModo.value === 'sitemap')
    return 'https://www.exemplo.com.br/sitemap.xml';
  if (siteModo.value === 'pagina')
    return 'https://www.exemplo.com.br/uma-pagina';
  return 'https://www.exemplo.com.br';
});

const TIPOS = computed(() => [
  {
    key: 'file',
    icon: 'i-lucide-file-text',
    label: t('POWER_DATA.ADD.TYPE_DOCUMENT'),
    hint: t('POWER_DATA.ADD.TYPE_DOCUMENT_HINT'),
  },
  {
    key: 'text',
    icon: 'i-lucide-type',
    label: t('POWER_DATA.ADD.TYPE_TEXT'),
    hint: t('POWER_DATA.ADD.TYPE_TEXT_HINT'),
  },
  {
    key: 'qa',
    icon: 'i-lucide-message-square-text',
    label: t('POWER_DATA.ADD.TYPE_QA'),
    hint: t('POWER_DATA.ADD.TYPE_QA_HINT'),
  },
  {
    key: 'site',
    icon: 'i-lucide-globe',
    label: isSuperAdmin.value
      ? t('POWER_DATA.ADD.TYPE_SITE')
      : t('POWER_DATA.ADD.TYPE_PAGE'),
    hint: isSuperAdmin.value
      ? t('POWER_DATA.ADD.TYPE_SITE_HINT')
      : t('POWER_DATA.ADD.TYPE_PAGE_HINT'),
  },
]);

const tipoAtual = computed(() => TIPOS.value.find(x => x.key === tipo.value));

// A dica muda com o tipo porque o alcance das tags muda: vários arquivos viram
// várias sources e cada uma leva as tags; um texto vira uma; e os pares de Q&A
// viram UMA fonte só, o que não era óbvio para quem estava preenchendo.
const dicaTags = computed(() => {
  if (tipo.value === 'qa') return t('POWER_DATA.ADD.TAGS_HINT_QA');
  if (tipo.value === 'site') return t('POWER_DATA.ADD.TAGS_HINT_SITE');
  if (tipo.value === 'text') return t('POWER_DATA.ADD.TAGS_HINT_TEXT');
  return t('POWER_DATA.ADD.TAGS_HINT_FILES');
});

const podeEnviar = computed(() => {
  if (isUploading.value) return false;
  if (tipo.value === 'file') return files.value.length > 0;
  if (tipo.value === 'text') return textContent.value.trim().length > 0;
  if (tipo.value === 'qa')
    return qaPairs.value.some(p => p.question.trim() && p.answer.trim());
  if (tipo.value === 'site') return sitePronto.value;
  return false;
});

function reset() {
  clearTimeout(descobertaTimer);
  descobertaTimer = null;
  isDiscovering.value = false;
  etapa.value = 'tipo';
  tipo.value = null;
  files.value = [];
  textContent.value = '';
  textFilename.value = '';
  qaPairs.value = [{ question: '', answer: '', tags: [] }];
  csvAviso.value = '';
  tags.value = [];
  errorMessage.value = '';
  batch.value = [];
  siteUrl.value = '';
  sitePages.value = [];
  siteSelecionadas.value = new Set();
  siteIncluir.value = '';
  siteExcluir.value = '';
  siteEngine.value = '';
  siteVia.value = '';
  siteModo.value = modoInicial();
  siteUrlsColadas.value = '';
  opcoesAbertas.value = false;
  opcExcluirTags.value = '';
  opcIncluirTags.value = '';
  opcCookies.value = '';
  opcSoPrincipal.value = true;
}

function escolher(item) {
  if (item.disabled) return;
  tipo.value = item.key;
  etapa.value = 'form';
}

function voltar() {
  etapa.value = 'tipo';
  errorMessage.value = '';
}

function addFiles(list) {
  const existentes = new Set(files.value.map(f => `${f.name}:${f.size}`));
  Array.from(list || []).forEach(file => {
    if (!existentes.has(`${file.name}:${file.size}`)) files.value.push(file);
  });
}

function onDrop(event) {
  isDragging.value = false;
  addFiles(event.dataTransfer?.files);
}

function onFilePicked(event) {
  addFiles(event.target.files);
  event.target.value = '';
}

function removeFile(i) {
  files.value.splice(i, 1);
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// O backend decide o formato pela extensão; um nome livre chegaria sem ela e
// voltaria como "Unsupported file type".
function comExtensao(nome, padrao) {
  const n = nome?.trim();
  if (!n) return padrao;
  return /\.[a-z0-9]+$/i.test(n) ? n : `${n}.txt`;
}

function montarForm(blob, filename, tagsDoItem = []) {
  const form = new FormData();
  form.append('file', blob, filename);
  // Tags do envio + tags do item, sem repetir. O Set tambem protege do caso
  // chato: a pessoa digita "garantia" no par e no lote, e a source ficaria com a
  // mesma tag duas vezes no indice lexical.
  const todas = [...new Set([...tags.value, ...tagsDoItem])];
  if (todas.length) form.append('tags', todas.join(','));
  // A origem só a tela sabe: texto e Q&A viajam como .txt.
  form.append('source_type', tipo.value);
  return form;
}

const paresPreenchidos = computed(
  () => qaPairs.value.filter(p => p.question.trim() && p.answer.trim()).length
);

function addQaPair() {
  qaPairs.value.push({ question: '', answer: '', tags: [] });
}

function removeQaPair(i) {
  if (qaPairs.value.length === 1) return;
  qaPairs.value.splice(i, 1);
}

// O nome e o que aparece na lista de sources, e a pergunta e o unico rotulo
// natural de um par. `.txt` nao e enfeite: o backend decide o formato pela
// extensao, e sem ela volta "Unsupported file type".
function nomeDoPar(pergunta) {
  const cru = pergunta
    .trim()
    .replace(/[^\p{L}\p{N} \-_,?!]/gu, '')
    .trim();
  // Corta na PALAVRA, nao no caractere: cortar seco produzia nomes como
  // "...eu quero um cupom Vocês tem algu.txt", que na lista parece dado
  // corrompido. Na tela o nome ainda ganha reticências por CSS quando nao cabe;
  // isto e para o nome GRAVADO nao nascer partido no meio de uma palavra.
  const limpo =
    cru.length <= 70
      ? cru
      : cru
          .slice(0, 70)
          .replace(/\s+\S*$/, '')
          .trim();
  return `${limpo || 'qa'}.txt`;
}

function itensDoEnvio() {
  if (tipo.value === 'file') {
    return files.value.map(f => ({ nome: f.name, blob: f, filename: f.name }));
  }
  if (tipo.value === 'text') {
    const filename = comExtensao(textFilename.value, 'text-source.txt');
    return [
      {
        nome: filename,
        blob: new Blob([textContent.value], { type: 'text/plain' }),
        filename,
      },
    ];
  }
  // UMA source por par, e nao um arquivo com tudo dentro. Os pares eram
  // concatenados num `qa-pairs.txt` unico, e por isso as tags nao podiam ser por
  // par: a source era uma so. Um par tambem e a unidade que a busca devolve, o
  // que faz cada linha da lista ser uma pergunta com nome proprio.
  return qaPairs.value
    .filter(p => p.question.trim() && p.answer.trim())
    .map(p => {
      const filename = nomeDoPar(p.question);
      return {
        nome: filename,
        blob: new Blob([`Q: ${p.question.trim()}\nA: ${p.answer.trim()}`], {
          type: 'text/plain',
        }),
        filename,
        tags: p.tags || [],
      };
    });
}

// Tags com IA para TODOS os pares preenchidos, de uma vez.
//
// Uma chamada por par, e de proposito: o modelo recebe um par inteiro e devolve
// tags daquele par. Mandar os 50 juntos sairia mais barato e introduziria o erro
// que ninguem percebe — tag do par 12 indo para o par 13 por desalinhamento.
//
// As sugestoes NAO sao gravadas: elas caem nos campos, editaveis, e vao junto no
// envio. Um modelo barato erra de vez em quando, e tag errada envenena a busca
// em silencio.
async function gerarTagsDosPares() {
  const indices = qaPairs.value
    .map((p, i) => (p.question.trim() && p.answer.trim() ? i : -1))
    .filter(i => i !== -1);
  if (!indices.length) return;

  gerandoTags.value = true;
  errorMessage.value = '';
  let falhas = 0;
  // ACESSO-01 (L8b): recusado pelo acesso, todo par é recusado igual, e a frase do servidor diz por quê.
  let recusa = '';
  // Sequencial, e nao Promise.all: um lote de 50 pares em paralelo satura o
  // agent-admin do mesmo jeito que o upload em lote saturava. `reduce` porque o
  // lint proibe for..of aqui (regenerator-runtime).
  await indices.reduce(
    (anterior, i) =>
      anterior.then(async () => {
        const par = qaPairs.value[i];
        try {
          const data = await powerDataAPI.suggestTagsFromText(
            accountId,
            props.storeId,
            `Q: ${par.question}\nA: ${par.answer}`
          );
          const sugeridas = data?.tags || [];
          // Soma, nao substitui: o que a pessoa ja digitou no par nao pode sumir
          // porque ela clicou num botao de sugestao.
          qaPairs.value[i].tags = [
            ...new Set([...(par.tags || []), ...sugeridas]),
          ];
        } catch (e) {
          falhas += 1;
          recusa = fraseDaRecusa(e) || recusa;
        }
      }),
    Promise.resolve()
  );
  gerandoTags.value = false;
  // Lista vazia NAO e falha: `sugerir_tags` devolve [] para par curto (menos de
  // 40 caracteres). Falha e a chamada ter morrido, e so isso vira aviso.
  if (falhas) {
    errorMessage.value =
      recusa || t('POWER_DATA.ADD.QA_TAGS_PARTIAL', { n: falhas });
  }
}

async function importarCsv(event) {
  const arquivo = event.target.files?.[0];
  event.target.value = '';
  if (!arquivo) return;
  csvAviso.value = '';
  errorMessage.value = '';
  try {
    const pares = lerCsvQa(await arquivo.text());
    if (!pares.length) {
      errorMessage.value = t('POWER_DATA.ADD.QA_CSV_VAZIO');
      return;
    }
    // Substitui os pares vazios e SOMA aos preenchidos: importar não pode apagar
    // o que a pessoa digitou antes de lembrar que tinha um arquivo.
    const digitados = qaPairs.value.filter(
      p => p.question.trim() || p.answer.trim()
    );
    qaPairs.value = [...digitados, ...pares];
    csvAviso.value = t('POWER_DATA.ADD.QA_CSV_OK', { n: pares.length });
  } catch {
    errorMessage.value = t('POWER_DATA.ADD.QA_CSV_ERRO');
  }
}

// Endereço repetido estoura no índice único do agent-admin e voltava como "serviço
// indisponível". A lista da base responde antes, e diz o que aconteceu.
async function paginaJaNaBase(url) {
  const data = await powerDataAPI.sourcesIndex(accountId, props.storeId);
  const fontes = data?.sources || data || [];
  const semBarra = u => String(u || '').replace(/\/+$/, '');
  return fontes.some(f => semBarra(f.scrape_url) === semBarra(url));
}

async function enviarSite() {
  isUploading.value = true;
  errorMessage.value = '';
  try {
    // Página única vai pelo endpoint de UMA fonte: ele não cria source-pai nem
    // filha, e não chama o mapeamento — ou seja, não gasta crédito de descoberta
    // para trazer a página que a pessoa já nomeou.
    if (siteModo.value === 'pagina') {
      const url = normalizarUrl(siteUrl.value);
      if (await paginaJaNaBase(url)) {
        errorMessage.value = t('POWER_DATA.ADD.SITE_PAGE_DUPLICATE');
        return;
      }
      const data = await powerDataAPI.sourcesScrape(
        accountId,
        props.storeId,
        url,
        opcoesDeScrape.value
      );
      if (data?.error) throw new Error(data.error?.message || 'scrape failed');
    } else {
      // Lista colada e descoberta terminam no MESMO endpoint: ele já aceitava uma
      // lista de URLs pronta. A diferença é só de onde a lista vem — do mapeamento
      // ou do que a pessoa colou —, e colar pula o crédito do mapeamento.
      const urls =
        siteModo.value === 'colar'
          ? urlsColadas.value
          : sitePagesFiltradas.value
              .filter(pg => siteSelecionadas.value.has(pg.url))
              .map(pg => pg.url);
      const data = await powerDataAPI.sourcesScrapeSite(
        accountId,
        props.storeId,
        {
          url: normalizarUrl(siteUrl.value) || urls[0],
          urls,
          tags: tags.value,
          opcoes: opcoesDeScrape.value,
        }
      );
      if (data?.error) throw new Error(data.error?.message || 'import failed');
    }
    emit('added');
    dialogRef.value?.close();
    reset();
  } catch (e) {
    // 504 é a página única que passou do teto: a busca segue do outro lado e a fonte costuma
    // aparecer em instantes. A lista recarrega, e a frase pede para conferir antes de repetir.
    // ACESSO-01 (L8b): recusada pelo acesso, a frase do servidor.
    if (fraseDaRecusa(e)) {
      errorMessage.value = fraseDaRecusa(e);
    } else if (e?.status === 504) {
      emit('added');
      errorMessage.value = t('POWER_DATA.ADD.SITE_PAGE_SLOW');
    } else if (e?.codigo === 'url_interna') {
      errorMessage.value = t('POWER_DATA.ADD.SITE_URL_INTERNA');
    } else {
      errorMessage.value = t('POWER_DATA.ADD.SITE_IMPORT_ERROR');
    }
  } finally {
    isUploading.value = false;
  }
}

// Áudio e vídeo vão para a rota que transcreve (PD-02); o resto, para a de sempre. O teto da
// mídia é conferido antes de subir, para não mandar 300 MB e ouvir "grande demais" no fim.
function enviarItem(item) {
  const form = montarForm(item.blob, item.filename, item.tags);
  if (tipo.value !== 'file' || !ehMidia(item.filename)) {
    return powerDataAPI.sourcesCreate(accountId, props.storeId, form);
  }
  if (item.blob.size > MAX_BYTES_MIDIA) {
    return Promise.reject(
      Object.assign(new Error('grande_demais'), { codigo: 'grande_demais' })
    );
  }
  return powerDataAPI.sourcesCreateMidia(accountId, props.storeId, form);
}

async function enviar() {
  if (!podeEnviar.value) return;
  if (tipo.value === 'site') {
    await enviarSite();
    return;
  }
  isUploading.value = true;
  errorMessage.value = '';

  const itens = itensDoEnvio();
  batch.value = itens.map(i => ({ nome: i.nome, estado: 'queued' }));
  // ACESSO-01 (L8b): recusado pelo acesso, todo item é recusado igual, e a frase do servidor diz por quê.
  let recusa = '';

  // Um de cada vez: paralelizar um lote grande já saturou o pool do agent-admin.
  for (let i = 0; i < itens.length; i += 1) {
    batch.value[i].estado = 'uploading';
    try {
      // eslint-disable-next-line no-await-in-loop
      await enviarItem(itens[i]);
      batch.value[i].estado = 'sent';
    } catch (erro) {
      batch.value[i].estado = 'failed';
      // PD-02: a recusa de mídia tem motivo (sem áudio, formato, tamanho), e ele aparece
      // ao lado do item em vez de sumir num "não foi enviado" genérico.
      batch.value[i].motivo = motivoDoErro(erro);
      recusa = fraseDaRecusa(erro) || recusa;
    }
  }

  isUploading.value = false;

  if (batch.value.every(b => b.estado === 'failed')) {
    errorMessage.value = recusa || t('POWER_DATA.ADD.ALL_FAILED');
    return;
  }
  // Parte dos arquivos falhou: a lista atualiza com o que entrou, e o diálogo fica aberto com
  // o motivo de cada falha (fechar escondia o que a pessoa precisava ler). Os que entraram saem
  // da seleção, para um novo "Enviar" não mandá-los de novo.
  if (tipo.value === 'file' && batch.value.some(b => b.estado === 'failed')) {
    emit('added');
    files.value = files.value.filter(
      (_, i) => batch.value[i]?.estado === 'failed'
    );
    errorMessage.value = t('POWER_DATA.ADD.SOME_FAILED');
    return;
  }
  emit('added');
  dialogRef.value?.close();
  reset();
}

defineExpose({
  // `pergunta`: abre direto no par de pergunta e resposta, com a pergunta escrita —
  // o atalho "Adicionar à base" da página Atendimento (REPORTS-02).
  open: ({ pergunta } = {}) => {
    reset();
    if (pergunta) {
      tipo.value = 'qa';
      etapa.value = 'form';
      qaPairs.value = [{ question: pergunta, answer: '', tags: [] }];
    }
    dialogRef.value?.open();
  },
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :width="etapa === 'tipo' ? 'lg' : tipo === 'site' ? '2xl' : 'xl'"
    :title="
      etapa === 'tipo'
        ? t('POWER_DATA.ADD.PICK_TYPE')
        : t('POWER_DATA.ADD.FORM_TITLE', { tipo: tipoAtual?.label || '' })
    "
    :show-confirm-button="etapa === 'form'"
    :confirm-button-label="t('POWER_DATA.ADD.SUBMIT')"
    :cancel-button-label="t('POWER_DATA.CANCEL')"
    :disable-confirm-button="!podeEnviar"
    :is-loading="isUploading"
    overflow-y-auto
    @confirm="enviar"
    @close="reset"
  >
    <div v-if="etapa === 'tipo'" class="grid grid-cols-2 gap-3">
      <button
        v-for="item in TIPOS"
        :key="item.key"
        class="flex flex-col items-start gap-1 p-4 text-start transition-colors border rounded-lg"
        :class="
          item.disabled
            ? 'border-n-weak bg-n-alpha-1 cursor-not-allowed opacity-60'
            : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
        "
        type="button"
        :disabled="item.disabled"
        @click="escolher(item)"
      >
        <div class="flex items-center w-full gap-2">
          <i :class="item.icon" class="text-n-slate-11" />
          <span class="text-body-main text-n-slate-12">{{ item.label }}</span>
          <span
            v-if="item.disabled"
            class="ltr:ml-auto rtl:mr-auto px-1.5 py-0.5 rounded text-body-small bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('POWER_DATA.ADD.COMING_SOON') }}
          </span>
        </div>
        <span class="text-body-small text-n-slate-11">{{ item.hint }}</span>
      </button>
    </div>

    <div v-else class="flex flex-col gap-4">
      <div>
        <Button
          :label="t('POWER_DATA.ADD.BACK_TO_TYPES')"
          type="button"
          icon="i-lucide-arrow-left"
          slate
          link
          xs
          @click="voltar"
        />
      </div>

      <div v-if="tipo === 'file'" class="flex flex-col gap-3">
        <label
          class="flex flex-col items-center justify-center gap-2 p-8 transition-colors border border-dashed rounded-lg cursor-pointer"
          :class="
            isDragging
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak hover:bg-n-alpha-1'
          "
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <i class="text-xl i-lucide-upload-cloud text-n-slate-10" />
          <span class="text-body-main text-n-slate-12">
            {{ t('POWER_DATA.ADD.DROP_HERE') }}
          </span>
          <span class="text-body-small text-n-slate-11">
            {{ t('POWER_DATA.ADD.DROP_HINT') }}
          </span>
          <!-- accept espelha ALLOWED_EXTENSIONS do agent-admin (agents.py) mais a mídia da
               PD-02 (helpers/midia.js): sem ele o seletor oferecia qualquer arquivo e o 400
               "Unsupported file type" só aparecia depois do upload. -->
          <input
            type="file"
            multiple
            :accept="ACCEPT_ARQUIVO"
            class="hidden"
            @change="onFilePicked"
          />
        </label>

        <ul v-if="files.length" class="flex flex-col gap-1">
          <li
            v-for="(file, index) in files"
            :key="`${file.name}-${index}`"
            class="flex items-center gap-2 px-3 py-2 rounded-md bg-n-alpha-1"
          >
            <i
              class="text-n-slate-10"
              :class="
                ehMidia(file.name)
                  ? 'i-lucide-file-audio'
                  : 'i-lucide-file-text'
              "
            />
            <span class="truncate text-body-main text-n-slate-12">
              {{ file.name }}
            </span>
            <span class="text-body-small text-n-slate-11">
              {{ formatSize(file.size) }}
            </span>
            <Button
              icon="i-lucide-x"
              type="button"
              slate
              xs
              class="ltr:ml-auto rtl:mr-auto"
              @click.prevent="removeFile(index)"
            />
          </li>
        </ul>
      </div>

      <div v-else-if="tipo === 'text'" class="flex flex-col gap-3">
        <Input
          v-model="textFilename"
          :label="t('POWER_DATA.ADD.TEXT_NAME')"
          :placeholder="t('POWER_DATA.ADD.TEXT_NAME_PLACEHOLDER')"
        />
        <TextArea
          v-model="textContent"
          :label="t('POWER_DATA.ADD.TEXT_CONTENT')"
          :placeholder="t('POWER_DATA.ADD.TEXT_CONTENT_PLACEHOLDER')"
          auto-height
          min-height="10rem"
          max-height="20rem"
        />
      </div>

      <div v-else-if="tipo === 'site'" class="flex flex-col gap-3">
        <!-- O saldo de créditos do motor de scraping NÃO aparece aqui, e a
             cadeia que o servia foi removida junto (cliente de API, controller,
             rota e endpoint). É saldo da nossa conta de fornecedor: número
             comercial nosso, que não é da conta do cliente. Esconder só a tela
             deixaria a rota legível por qualquer admin da conta. -->
        <div v-if="MODOS_DE_SITE.length > 1" class="flex flex-wrap gap-1">
          <Button
            v-for="modo in MODOS_DE_SITE"
            :key="modo.key"
            :label="modo.label"
            type="button"
            size="sm"
            :faded="siteModo === modo.key"
            :slate="siteModo !== modo.key"
            @click="siteModo = modo.key"
          />
        </div>
        <p class="text-body-small text-n-slate-11">{{ dicaDoModo }}</p>

        <!-- Lista colada: nenhuma descoberta, nenhum crédito de mapeamento. -->
        <TextArea
          v-if="siteModo === 'colar'"
          v-model="siteUrlsColadas"
          :label="
            t('POWER_DATA.ADD.SITE_PASTE_URLS', { n: urlsColadas.length })
          "
          placeholder="https://exemplo.com.br/pagina-1&#10;https://exemplo.com.br/pagina-2"
          auto-height
          min-height="6rem"
          max-height="14rem"
        />

        <div v-else class="flex items-end gap-2">
          <Input
            v-model="siteUrl"
            class="flex-1"
            :label="rotuloDaUrl"
            :placeholder="placeholderDaUrl"
            @enter="siteModo === 'pagina' ? null : descobrirPaginas()"
          />
          <Button
            v-if="siteModo !== 'pagina'"
            :label="t('POWER_DATA.ADD.SITE_DISCOVER')"
            type="button"
            icon="i-lucide-search"
            slate
            size="sm"
            class="mb-px"
            :disabled="!siteUrl.trim()"
            :is-loading="isDiscovering"
            @click="descobrirPaginas"
          />
        </div>

        <!-- Opções de limpeza e acesso. Fechado por padrão: o padrão do motor
             serve para a maioria, e campo aberto convida a mexer sem precisar. -->
        <!-- Cookies e seletores CSS são ajuste de plataforma, não do cliente. -->
        <div v-if="isSuperAdmin" class="border rounded-lg border-n-weak">
          <button
            type="button"
            class="flex items-center w-full gap-2 px-3 py-2 text-start"
            @click="opcoesAbertas = !opcoesAbertas"
          >
            <i
              :class="
                opcoesAbertas
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
              class="text-n-slate-10"
            />
            <span class="text-body-small text-n-slate-12">
              {{ t('POWER_DATA.ADD.SITE_OPTIONS') }}
            </span>
            <span
              v-if="opcoesDeScrape"
              class="ltr:ml-auto rtl:mr-auto px-1.5 py-0.5 rounded text-body-small bg-n-alpha-2 text-n-slate-11"
            >
              {{ t('POWER_DATA.ADD.SITE_OPTIONS_ON') }}
            </span>
          </button>
          <div
            v-if="opcoesAbertas"
            class="flex flex-col gap-2 px-3 py-3 border-t border-n-weak"
          >
            <div class="grid grid-cols-2 gap-2">
              <Input
                v-model="opcExcluirTags"
                size="sm"
                :label="t('POWER_DATA.ADD.SITE_EXCLUDE_TAGS')"
                placeholder="script, .ad, #footer"
              />
              <Input
                v-model="opcIncluirTags"
                size="sm"
                :label="t('POWER_DATA.ADD.SITE_INCLUDE_TAGS')"
                placeholder="article, main"
              />
            </div>
            <p class="text-body-small text-n-slate-11">
              {{ t('POWER_DATA.ADD.SITE_INCLUDE_TAGS_HINT') }}
            </p>
            <Input
              v-model="opcCookies"
              size="sm"
              :label="t('POWER_DATA.ADD.SITE_COOKIES')"
              placeholder="nome=valor; nome2=valor2"
            />
            <p class="text-body-small text-n-amber-11">
              {{ t('POWER_DATA.ADD.SITE_COOKIES_HINT') }}
            </p>
            <label class="flex items-center gap-2">
              <Checkbox v-model="opcSoPrincipal" />
              <span class="text-body-small text-n-slate-12">
                {{ t('POWER_DATA.ADD.SITE_MAIN_ONLY') }}
              </span>
            </label>
          </div>
        </div>

        <template v-if="sitePages.length && siteModo !== 'pagina'">
          <div class="grid grid-cols-2 gap-2">
            <Input
              v-model="siteIncluir"
              size="sm"
              :label="t('POWER_DATA.ADD.SITE_INCLUDE')"
              placeholder="/produtos, /ajuda"
            />
            <Input
              v-model="siteExcluir"
              size="sm"
              :label="t('POWER_DATA.ADD.SITE_EXCLUDE')"
              placeholder="/en, /es, /blog"
            />
          </div>

          <div class="border rounded-lg border-n-weak">
            <div
              class="flex items-center gap-2 px-3 py-2 border-b border-n-weak bg-n-alpha-1"
            >
              <Checkbox
                :model-value="siteTodasMarcadas"
                :indeterminate="sitePronto && !siteTodasMarcadas"
                @change="toggleSiteTodas"
              />
              <span class="text-body-small text-n-slate-11 tabular-nums">
                {{
                  t('POWER_DATA.ADD.SITE_COUNT', {
                    sel: sitePagesFiltradas.filter(pg =>
                      siteSelecionadas.has(pg.url)
                    ).length,
                    total: sitePagesFiltradas.length,
                    found: sitePages.length,
                  })
                }}
              </span>
            </div>
            <ul class="overflow-y-auto divide-y divide-n-weak max-h-64">
              <li
                v-for="pg in sitePagesFiltradas"
                :key="pg.url"
                class="flex items-start gap-2 px-3 py-1.5 cursor-pointer hover:bg-n-alpha-1"
                @click="toggleSitePagina(pg.url)"
              >
                <Checkbox
                  :model-value="siteSelecionadas.has(pg.url)"
                  class="mt-0.5"
                  @click.stop
                  @change="toggleSitePagina(pg.url)"
                />
                <div class="flex flex-col min-w-0">
                  <span class="truncate text-body-main text-n-slate-12">
                    {{ pg.title || caminhoDe(pg.url) }}
                  </span>
                  <span class="truncate text-body-small text-n-slate-10">
                    {{ caminhoDe(pg.url) }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </template>
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="(pair, index) in qaPairs"
          :key="index"
          data-qa-pair
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak"
        >
          <div class="flex items-center justify-between">
            <span class="text-body-small text-n-slate-11">
              {{ t('POWER_DATA.ADD.QA_PAIR', { n: index + 1 }) }}
            </span>
            <Button
              v-if="qaPairs.length > 1"
              type="button"
              icon="i-lucide-trash-2"
              slate
              xs
              @click="removeQaPair(index)"
            />
          </div>
          <Input
            v-model="pair.question"
            :placeholder="t('POWER_DATA.ADD.QA_QUESTION')"
          />
          <TextArea
            v-model="pair.answer"
            :placeholder="t('POWER_DATA.ADD.QA_ANSWER')"
            auto-height
            min-height="4rem"
            max-height="12rem"
          />
          <div
            class="px-3 py-2 transition-colors border rounded-lg border-n-weak bg-n-alpha-1 focus-within:border-n-brand"
          >
            <TagInput
              v-model="pair.tags"
              allow-create
              :auto-open-dropdown="false"
              :placeholder="t('POWER_DATA.ADD.QA_TAGS_PLACEHOLDER')"
            />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            :label="t('POWER_DATA.ADD.QA_ADD_PAIR')"
            type="button"
            icon="i-lucide-plus"
            slate
            size="sm"
            @click="addQaPair"
          />
          <!-- Sugere para TODOS os pares preenchidos. O rótulo diz quantos vão
               receber, porque "gerar tags" sem número não avisa que são 50
               chamadas ao modelo. -->
          <Button
            :label="
              t('POWER_DATA.ADD.QA_GENERATE_TAGS', { n: paresPreenchidos })
            "
            type="button"
            icon="i-lucide-sparkles"
            faded
            size="sm"
            :disabled="!paresPreenchidos || gerandoTags"
            :is-loading="gerandoTags"
            @click="gerarTagsDosPares"
          />
          <Button
            :label="t('POWER_DATA.ADD.QA_IMPORT_CSV')"
            type="button"
            icon="i-lucide-upload"
            slate
            size="sm"
            @click="csvInput?.click()"
          />
          <input
            ref="csvInput"
            type="file"
            accept=".csv,text/csv"
            class="hidden"
            @change="importarCsv"
          />
          <span v-if="csvAviso" class="text-body-small text-n-teal-11">
            {{ csvAviso }}
          </span>
        </div>
      </div>

      <!-- A página única não leva tags: o endpoint dela as descarta, e o campo prometia
           aplicá-las. -->
      <div
        v-if="!(tipo === 'site' && siteModo === 'pagina')"
        class="flex flex-col gap-1"
      >
        <label class="text-body-main text-n-slate-12">
          {{ t('POWER_DATA.ADD.BATCH_TAGS') }}
        </label>
        <div
          class="px-3 py-2 transition-colors border rounded-lg border-n-weak bg-n-alpha-1 focus-within:border-n-brand"
        >
          <TagInput
            v-model="tags"
            allow-create
            :auto-open-dropdown="false"
            :placeholder="t('POWER_DATA.SOURCES.TAGS_PLACEHOLDER')"
          />
        </div>
        <p class="text-body-small text-n-slate-11">
          {{ dicaTags }}
        </p>
      </div>

      <p v-if="errorMessage" class="text-body-main text-n-ruby-11">
        {{ errorMessage }}
      </p>

      <ul v-if="batch.length" class="flex flex-col gap-1">
        <li
          v-for="(item, index) in batch"
          :key="`${item.nome}-${index}`"
          class="flex items-center gap-2 px-3 py-2 rounded-md bg-n-alpha-1"
        >
          <i
            :class="{
              'i-lucide-check text-n-teal-11': item.estado === 'sent',
              'i-lucide-loader-2 animate-spin text-n-amber-11':
                item.estado === 'uploading',
              'i-lucide-clock text-n-slate-10': item.estado === 'queued',
              'i-lucide-alert-circle text-n-ruby-11': item.estado === 'failed',
            }"
          />
          <span class="truncate text-body-main text-n-slate-12">
            {{ item.nome }}
          </span>
          <span
            v-if="item.estado === 'failed' && item.motivo"
            class="text-body-small text-n-ruby-11 ltr:ml-auto rtl:mr-auto"
          >
            {{ t(`POWER_DATA.MIDIA.MOTIVO.${item.motivo.toUpperCase()}`) }}
          </span>
        </li>
      </ul>
    </div>
  </Dialog>
</template>
